import { useState } from 'react';
import { NetflixRow, NetflixCard } from './NetflixRow';
import { CardDetailModal } from './CardDetailModal';

export const ExpertiseRow = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const expertise = [
    {
      title: "Strategic Technology Risk Leadership",
      subtitle: "Enterprise Risk Governance",
      description: "15+ years orchestrating enterprise-wide risk frameworks across the full spectrum of technology operations. Expert in technology risk quantification using EML/PML methodologies, board-level risk communication, and cross-functional risk remediation program leadership spanning 1st, 2nd, and 3rd lines of defense.",
      match: "Executive Level",
      tags: ["Risk Governance", "EML/PML", "Board Communication"],
      imageSrc: "https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg",
      businessImpact: "Typically achieves 40% risk reduction and 60% regulatory penalty reduction",
      keyCap: [
        "Technology Risk Assessment & Quantification (EML/PML)",
        "Board-Level Risk Communication & Reporting",
        "Enterprise Risk Framework Development",
        "Cross-Functional Risk Remediation Programs",
        "Three Lines of Defense Implementation"
      ]
    },
    {
      title: "Cloud Security & Governance",
      subtitle: "Multi-Cloud Architecture",
      description: "Architecting secure, compliant cloud environments for global enterprises. Specialized in multi-cloud security architecture, shared responsibility models, FedRAMP authorization acceleration (20% timeline reduction achieved), and Cloud Controls Matrix (CCM) framework implementation with concentration risk mitigation.",
      match: "Expert Level",
      tags: ["FedRAMP", "CCM", "Multi-Cloud"],
      imageSrc: "https://images.unsplash.com/photo-1647214648081-471aefb5ed86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjbG91ZCUyMHNlY3VyaXR5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHxibHVlfDE3NjAzNDAzMjR8MA&ixlib=rb-4.1.0&q=85",
      businessImpact: "Delivers 50% security incident reduction and 30% faster digital transformation",
      keyCap: [
        "Multi-Cloud Security Architecture Design",
        "Shared Responsibility Model Implementation",
        "FedRAMP Authorization & Compliance Acceleration",
        "Cloud Controls Matrix (CCM) Framework",
        "Concentration Risk Assessment & Mitigation"
      ]
    },
    {
      title: "AI Safety & Governance",
      subtitle: "Responsible AI Deployment",
      description: "Pioneering AI risk management frameworks for responsible AI deployment at scale. Expert in EU AI Act compliance implementation, AI model traceability, production approval gates, Gemini & GenAI product safety program governance, and AI controls framework development for enterprise environments.",
      match: "Innovation Leader",
      tags: ["EU AI Act", "GenAI Safety", "AI Controls"],
      imageSrc: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      businessImpact: "Achieves 80% AI risk reduction and full regulatory compliance",
      keyCap: [
        "AI Model Risk Assessment & Categorization",
        "EU AI Act Compliance Implementation",
        "AI Production Approval Gates & Traceability",
        "GenAI Safety Control Framework Development",
        "AI Ethics & Bias Mitigation Strategies"
      ]
    },
    {
      title: "Regulatory Compliance Excellence",
      subtitle: "Zero-Defect Compliance",
      description: "Navigating complex global regulatory landscapes with zero-defect compliance strategies. Specialized in DORA (Digital Operational Resilience Act) implementation, ISO 27001/SOX/GDPR compliance frameworks, NYDFS cybersecurity approval (zero findings achieved), and cross-border privacy alignment.",
      match: "Compliance Expert",
      tags: ["DORA", "ISO 27001", "NYDFS"],
      imageSrc: "https://images.unsplash.com/photo-1559925558-15771f359c4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxjbG91ZCUyMHNlY3VyaXR5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHxibHVlfDE3NjAzNDAzMjR8MA&ixlib=rb-4.1.0&q=85",
      businessImpact: "Delivers 90% regulatory risk reduction and enables confident global expansion",
      keyCap: [
        "DORA Digital Resilience Framework Implementation",
        "ISO 27001/SOX/GDPR Compliance Programs",
        "NYDFS Cybersecurity Regulation Alignment",
        "Cross-Border Privacy & Data Protection",
        "Regulatory Examination & Audit Readiness"
      ]
    },
    {
      title: "Continuous Controls Monitoring",
      subtitle: "Real-Time Risk Visibility",
      description: "Building real-time visibility and automated assurance across technology environments. Expert in ConMon implementation for restricted developer access, data residency program automation, OSCAL-based machine-readable control evidence, and real-time risk posture visualization.",
      match: "Technical Expert",
      tags: ["ConMon", "OSCAL", "Automation"],
      imageSrc: "https://images.pexels.com/photos/5475811/pexels-photo-5475811.jpeg",
      businessImpact: "Reduces incident response time by 75% and audit preparation costs by 40%",
      keyCap: [
        "Continuous Monitoring (ConMon) System Design",
        "OSCAL Machine-Readable Control Evidence",
        "Real-Time Risk Posture Dashboards",
        "Automated Control Testing & Validation",
        "Data Residency & Sovereignty Monitoring"
      ]
    },
    {
      title: "Product Security Integration",
      subtitle: "Security-by-Design",
      description: "Embedding security controls into product development lifecycles from design to deployment. Specialized in security requirements integration, threat modeling, DevSecOps pipeline controls, and Red Flags Identity Theft Prevention (30% fraud detection improvement achieved).",
      match: "Product Security",
      tags: ["DevSecOps", "Threat Modeling", "Red Flags"],
      imageSrc: "https://images.unsplash.com/photo-1593407089396-93f0c7a575f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBjeWJlcnNlY3VyaXR5JTIwYmFua2luZ3xlbnwwfHx8Ymx1ZXwxNzYwMzQwMzE0fDA&ixlib=rb-4.1.0&q=85",
      businessImpact: "Achieves 70% vulnerability reduction and accelerates secure product delivery",
      keyCap: [
        "Security Requirements Integration (SDLC)",
        "Threat Modeling & Risk Assessment",
        "DevSecOps Pipeline Security Controls",
        "Red Flags Identity Theft Prevention",
        "Secure Code Review & Vulnerability Management"
      ]
    }
  ];

  const handleCardClick = (expertiseArea) => {
    setSelectedCard(expertiseArea);
    setModalOpen(true);
  };

  return (
    <>
      <NetflixRow title="Core Expertise" id="expertise">
        {expertise.map((exp, index) => (
          <NetflixCard
            key={index}
            title={exp.title}
            subtitle={exp.subtitle}
            description={exp.description}
            match={exp.match}
            tags={exp.tags}
            imageSrc={exp.imageSrc}
            onClick={() => handleCardClick(exp)}
          />
        ))}
      </NetflixRow>
      
      <CardDetailModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        card={selectedCard}
        type="expertise"
      />
    </>
  );
};